import type { ProdRow } from "../components/OSProductivityTable";
import { parseDecimalInput } from "./decimalInput";

export interface ProductivityAggregateRow extends ProdRow {
  aggregate: true;
  sourceIndices: number[];
  machineLabel: string;
  montageRnTime: string;
  machineRnTime: string;
  dismountRnTime: string;
  cleaningRnTime: string;
  totalRnTime: string;
  total: string;
  unit: string;
  // True when ANY Почеток/Крај pair in this group (across all four timing stages) had a
  // negative duration (end before start — real, deliberately-visible bad data on detail
  // rows, see durationMinutes in OSProductivityTable). A negative value is invalid, not a
  // duration: it is excluded from every sum below rather than silently subtracting minutes.
  hasInvalidDuration: boolean;
  // Highest per-machine operatorCount in the group (0 when none). Exists only so the
  // table can sort this column numerically, since operatorCount itself can now hold a
  // non-numeric distribution string like "2, 3".
  operatorCountMax: number;
  // Minutes between the EARLIEST and LATEST timestamp found anywhere in the group, across
  // all eight Почеток/Крај fields and every machine row — the actual wall-clock span of the
  // subprocess, distinct from totalRnTime (the SUM of per-machine durations, which can be
  // larger than the span when machines run in parallel, or equal to it when they run back
  // to back). It is a min/max span, so — unlike totalRnTime — it can never go negative and
  // is immune to a single inverted pair; no invalid-duration flagging applies to it.
  elapsedRnTime: string;
}

interface BuildProductivityAggregateRowsParams {
  rows: ProdRow[];
  subprocessLabelOf: (subprocessId: string) => string;
  unitOf: (machineId: string | null) => string;
  durationMinutes: (start: string | undefined, end: string | undefined) => number | null;
  parseDateTime: (value: unknown) => Date | null;
}

interface DatePick {
  value: string;
  time: number;
}

const num = (value: unknown): number => parseDecimalInput(value) ?? 0;
const plannedSum = (total: number): string => (total > 0 ? String(total) : "");
const actualSum = (total: number, count: number): string => (count > 0 ? String(total) : "");

// The eight Почеток/Крај fields elapsedSpan() scans — every timing stage's start and end,
// across every machine row in the group.
const ELAPSED_FIELDS: Array<keyof ProdRow> = [
  "montageStart", "montageEnd",
  "machineStart", "machineEnd",
  "dismountStart", "dismountEnd",
  "cleaningStart", "cleaningEnd",
];

// Minutes between the earliest and latest parseable timestamp across all eight fields of
// every row in the group. Unparseable/empty values are skipped; fewer than two parseable
// timestamps in the whole group means there is no span to report, so "" (not "0").
const elapsedSpan = (
  groupRows: ProdRow[],
  parseDateTime: (value: unknown) => Date | null
): string => {
  let min: number | null = null;
  let max: number | null = null;
  let count = 0;
  groupRows.forEach((row) => {
    ELAPSED_FIELDS.forEach((field) => {
      const date = parseDateTime(row[field] as string | undefined);
      if (!date) return;
      const time = date.getTime();
      count += 1;
      if (min === null || time < min) min = time;
      if (max === null || time > max) max = time;
    });
  });
  if (count < 2 || min === null || max === null) return "";
  return String(Math.round((max - min) / 60000));
};

const earlier = (current: DatePick | null, next: DatePick): DatePick =>
  !current || next.time < current.time ? next : current;

const later = (current: DatePick | null, next: DatePick): DatePick =>
  !current || next.time > current.time ? next : current;

const pickDate = (
  current: DatePick | null,
  value: string | undefined,
  parseDateTime: (value: unknown) => Date | null,
  pick: (current: DatePick | null, next: DatePick) => DatePick
): DatePick | null => {
  const date = parseDateTime(value);
  if (!date || !value) return current;
  return pick(current, { value, time: date.getTime() });
};

export function buildProductivityAggregateRows(params: BuildProductivityAggregateRowsParams): ProductivityAggregateRow[] {
  const { rows, subprocessLabelOf, unitOf, durationMinutes, parseDateTime } = params;
  const groups = new Map<string, ProdRow[]>();

  rows.forEach((row) => {
    const key = row.subprocessId || "";
    const group = groups.get(key);
    if (group) group.push(row);
    else groups.set(key, [row]);
  });

  return Array.from(groups.entries()).map(([subprocessId, groupRows]) => {
    const sourceIndices = groupRows.map((row) => rows.indexOf(row));
    // Shared across sumDuration()/totalDuration() below — a negative pair (end before
    // start) is invalid data, not a duration: skipped from every total, but flagged so
    // the UI can say a value was excluded instead of the sum quietly shrinking.
    let groupHasInvalid = false;
    const sumDuration = (startField: keyof ProdRow, endField: keyof ProdRow) => {
      let total = 0;
      let count = 0;
      groupRows.forEach((row) => {
        const value = durationMinutes(row[startField] as string | undefined, row[endField] as string | undefined);
        if (value === null) return;
        if (value < 0) {
          groupHasInvalid = true;
          return;
        }
        total += value;
        count += 1;
      });
      return actualSum(total, count);
    };
    const totalDuration = () => {
      let total = 0;
      let count = 0;
      const pairs: Array<[keyof ProdRow, keyof ProdRow]> = [
        ["montageStart", "montageEnd"],
        ["machineStart", "machineEnd"],
        ["dismountStart", "dismountEnd"],
        ["cleaningStart", "cleaningEnd"],
      ];
      pairs.forEach(([startField, endField]) => {
        groupRows.forEach((row) => {
          const value = durationMinutes(row[startField] as string | undefined, row[endField] as string | undefined);
          if (value === null) return;
          if (value < 0) {
            groupHasInvalid = true;
            return;
          }
          total += value;
          count += 1;
        });
      });
      return actualSum(total, count);
    };

    const firstStart = (field: keyof ProdRow) =>
      groupRows.reduce<DatePick | null>((current, row) => pickDate(current, row[field] as string | undefined, parseDateTime, earlier), null)?.value || "";
    const lastEnd = (field: keyof ProdRow) =>
      groupRows.reduce<DatePick | null>((current, row) => pickDate(current, row[field] as string | undefined, parseDateTime, later), null)?.value || "";

    const montageTime = groupRows.reduce((sum, row) => sum + num(row.montageTime), 0);
    const machineTime = groupRows.reduce((sum, row) => sum + num(row.machineTime), 0);
    const cleaningTime = groupRows.reduce((sum, row) => sum + num(row.cleaningTime), 0);
    const dismountCleanTime = groupRows.reduce((sum, row) => sum + num(row.dismountCleanTime), 0);
    // operatorCount is deliberately NOT summed across machines: the same 2 people usually
    // run 3 machines one after another, so a total of 6 would be false by default. It also
    // feeds nothing downstream (no costing/payroll/capacity maths) — display-only, so the
    // honest value is the distinct per-machine distribution, not a fabricated total.
    const operatorCountValues = Array.from(
      new Set(
        groupRows
          .map((row) => num(row.operatorCount))
          .filter((value) => value > 0)
      )
    ).sort((a, b) => a - b);
    const operatorCount = operatorCountValues.join(", ");
    const operatorCountMax = operatorCountValues.length > 0 ? operatorCountValues[operatorCountValues.length - 1] : 0;
    const units = Array.from(new Set(groupRows.map((row) => unitOf(row.machineId)).filter(Boolean)));

    return {
      aggregate: true,
      sourceIndices,
      machineLabel: subprocessLabelOf(subprocessId),
      machineId: null,
      montageTime: plannedSum(montageTime),
      machineTime: plannedSum(machineTime),
      cleaningTime: plannedSum(cleaningTime),
      dismountCleanTime: plannedSum(dismountCleanTime),
      operatorCount,
      total: plannedSum(montageTime + machineTime + cleaningTime + dismountCleanTime),
      unit: units.length === 1 ? units[0] : "",
      setupParams: [],
      extraSetupParams: [],
      subprocessId,
      montageStart: firstStart("montageStart"),
      montageEnd: lastEnd("montageEnd"),
      montageRnTime: sumDuration("montageStart", "montageEnd"),
      machineStart: firstStart("machineStart"),
      machineEnd: lastEnd("machineEnd"),
      machineRnTime: sumDuration("machineStart", "machineEnd"),
      dismountStart: firstStart("dismountStart"),
      dismountEnd: lastEnd("dismountEnd"),
      dismountRnTime: sumDuration("dismountStart", "dismountEnd"),
      cleaningStart: firstStart("cleaningStart"),
      cleaningEnd: lastEnd("cleaningEnd"),
      cleaningRnTime: sumDuration("cleaningStart", "cleaningEnd"),
      totalRnTime: totalDuration(),
      // Read only after every sumDuration()/totalDuration() call above has run, so it
      // reflects all four pairs regardless of which per-pair sum the negative fell into.
      hasInvalidDuration: groupHasInvalid,
      operatorCountMax,
      elapsedRnTime: elapsedSpan(groupRows, parseDateTime),
    };
  });
}
